import React, { useState } from "react";
import { toast } from "sonner";
import { Gauge, Info, X } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";
import api, { apiError } from "@/lib/api";
import RaceExperience from "@/components/RaceExperience";

const TRACKS = ["alphabetical", "numerical", "alphanumeric", "special"];
const PENALTIES = [1, 2, 5];

function Tutorial({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" data-testid="tutorial-overlay">
      <div className="bg-race-surface border border-race-red/40 max-w-lg w-full p-6 noise relative">
        <button onClick={onClose} className="absolute top-4 right-4 text-race-muted hover:text-race-txt" data-testid="tutorial-close"><X size={20} /></button>
        <div className="overline text-race-red mb-2">First race - read this</div>
        <h2 className="font-heading text-4xl tracking-tight mb-4">HOW TEXT RACING WORKS</h2>
        <ul className="space-y-3 text-sm text-race-sub mb-6">
          <li><span className="text-race-txt font-semibold">Type on the custom keyboard.</span> The device keyboard is disabled - no autocorrect, no paste.</li>
          <li><span className="text-race-red font-semibold">There is NO delete key.</span> A wrong keystroke can't be fixed - it adds a time penalty instead.</li>
          <li><span className="text-race-volt font-semibold">Score = CPS.</span> Characters per second on your penalty-adjusted final time. Faster + cleaner wins.</li>
          <li>The <span className="text-race-txt font-semibold">www.</span> and <span className="text-race-txt font-semibold">.com</span> buttons are always on the keyboard.</li>
        </ul>
        <button onClick={onClose} data-testid="tutorial-start"
          className="w-full bg-race-red hover:bg-race-redhover text-race-bg font-heading text-2xl tracking-wide py-3 rounded-sm transition-colors">
          GOT IT - TO THE GRID
        </button>
      </div>
    </div>
  );
}

export default function Practice() {
  const { settings, update } = useSettings();
  const [race, setRace] = useState(null);
  const [format, setFormat] = useState("sprint");
  const [track, setTrack] = useState("alphabetical");
  const [penalty, setPenalty] = useState(2);
  const [busy, setBusy] = useState(false);
  const [tutorial, setTutorial] = useState(!localStorage.getItem("tr_tutorial_seen"));

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/practice/new", {
        race_type: format,
        track_type: track,
        penalty_seconds: penalty,
      });
      setRace(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const closeTutorial = () => {
    localStorage.setItem("tr_tutorial_seen", "1");
    setTutorial(false);
  };

  if (race) {
    return (
      <>
        {tutorial && <Tutorial onClose={closeTutorial} />}
        <RaceExperience
          race={race}
          onExit={() => setRace(null)}
          onAgain={() => { setRace(null); setTimeout(start, 50); }}
        />
      </>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-slide-up" data-testid="practice-setup">
      {tutorial && <Tutorial onClose={closeTutorial} />}
      <div className="flex items-center gap-3 mb-2">
        <Gauge className="text-race-red" size={28} />
        <h1 className="font-heading text-5xl tracking-tight">PRACTICE PADDOCK</h1>
      </div>
      <p className="text-race-sub mb-8 flex items-center gap-2 text-sm">
        <Info size={14} /> Free & unlimited. No BR License required. Race calibrated ghost racers.
      </p>

      <div className="bg-race-surface border border-race-line p-6 flex flex-col gap-6">
        <Field label="Race Format">
          {[["sprint", "Sprint - type the phrase"], ["trivia", "Trivia - answer the question"]].map(([v, l]) => (
            <Chip key={v} active={format === v} onClick={() => setFormat(v)} testid={`format-${v}`}>{l}</Chip>
          ))}
        </Field>

        <Field label="Track Type (character set)">
          {TRACKS.map((t) => (
            <Chip key={t} active={track === t} onClick={() => setTrack(t)} testid={`track-${t}`}>{t}</Chip>
          ))}
        </Field>

        <Field label="Penalty per error">
          {PENALTIES.map((p) => (
            <Chip key={p} active={penalty === p} onClick={() => setPenalty(p)} testid={`penalty-${p}`}>{p}s</Chip>
          ))}
        </Field>

        <Field label="Practice options">
          <Chip active={settings.ghostRacers !== "off"}
            onClick={() => update("ghostRacers", settings.ghostRacers === "off" ? "on" : "off")}
            testid="toggle-ghosts-practice">
            Ghost racers {settings.ghostRacers === "off" ? "off" : "on"}
          </Chip>
          <Chip active={settings.keyGuidance !== "off"}
            onClick={() => update("keyGuidance", settings.keyGuidance === "off" ? "on" : "off")}
            testid="toggle-guidance-practice">
            Keyboard light-up {settings.keyGuidance === "off" ? "off" : "on"}
          </Chip>
        </Field>
        <p className="text-xs text-race-muted -mt-1">Ghost racers and keyboard light-up are practice-only. Real IBRA contests are humans-only with no assist.</p>

        <button onClick={start} disabled={busy} data-testid="launch-practice-btn"
          className="bg-race-red hover:bg-race-redhover disabled:opacity-50 text-race-bg font-heading text-3xl tracking-wide py-4 rounded-sm transition-colors">
          {busy ? "GENERATING…" : "TO THE STARTING LINE"}
        </button>
      </div>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="overline mb-2">{label}</div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

const Chip = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-4 py-2 rounded-sm text-sm font-medium border transition-colors capitalize ${
      active ? "bg-race-red border-race-red text-race-bg" : "bg-race-bg border-race-line text-race-sub hover:border-race-txt"
    }`}>
    {children}
  </button>
);
